import cartService from '../../services/cartService';

// Action Types
export const ADD_TO_CART = "ADD_TO_CART";
export const DECREASE_QUANTITY = "DECREASE_QUANTITY";
export const DELETE_FROM_CART = "DELETE_FROM_CART";
export const DELETE_ALL_FROM_CART = "DELETE_ALL_FROM_CART";
export const SET_CART_FROM_BACKEND = "SET_CART_FROM_BACKEND";
export const CART_LOADING = "CART_LOADING";
export const CART_ERROR = "CART_ERROR";
export const UPDATE_CART_ITEM = "UPDATE_CART_ITEM";
export const UPSERT_CART_ITEM = "UPSERT_CART_ITEM";

/**
 * Fetch cart from backend and sync to Redux
 */
export const fetchCart = () => {
  return async dispatch => {
    dispatch({ type: CART_LOADING, payload: true });
    try {
      const result = await cartService.getCart();
      if (result.success && result.cart) {
        dispatch({
          type: SET_CART_FROM_BACKEND,
          payload: {
            items: result.cart.items || [],
            totals: result.totals || null
          }
        });
      }
      dispatch({ type: CART_LOADING, payload: false });
      return result;
    } catch (error) {
      dispatch({ type: CART_ERROR, payload: error.message });
      dispatch({ type: CART_LOADING, payload: false });
      return { success: false, error: error.message };
    }
  };
};

/**
 * Add item to cart via backend API
 * @param {Object} product - Product object with id, name, price, etc.
 * @param {Function} addToast - Toast notification function
 * @param {number} quantity - Quantity to add
 */
export const addToCartAsync = (product, addToast, quantity = 1) => {
  return async dispatch => {
    try {
      const productId = product.id || product.product_id;
      if (!productId) {
        return {
          success: false,
          error: 'Missing product id'
        };
      }

      const result = await cartService.addToCart(productId, quantity);

      if (result.success) {
        if (result.data?.cart?.items) {
          dispatch({
            type: SET_CART_FROM_BACKEND,
            payload: {
              items: result.data.cart.items,
              totals: result.data.totals || result.data.cart.totals || null
            }
          });
        }
        const cartItemPayload = result.item || result.data?.cart_item;
        if (cartItemPayload) {
          dispatch({
            type: UPSERT_CART_ITEM,
            payload: cartItemPayload
          });
        }
        // Confirm that the backend session retained the item before reporting success.
        // Privacy-focused browsers may accept the POST while rejecting a cross-origin
        // session cookie, which otherwise produces a misleading success toast.
        const refreshedCart = await dispatch(fetchCart());
        const persistedItems = refreshedCart?.cart?.items || [];
        const itemPersisted = persistedItems.some(item => {
          const persistedProductId = item.product?.id || item.product_id || item.product || item.id;
          return String(persistedProductId) === String(productId);
        });

        if (!refreshedCart?.success || !itemPersisted) {
          const persistenceError = "Cart could not be saved. Please retry.";
          if (addToast) {
            addToast(persistenceError, { appearance: "error", autoDismiss: true });
          }
          return { success: false, error: "Cart session did not persist" };
        }

        if (addToast) {
          addToast("Added to Cart", { appearance: "success", autoDismiss: true });
        }
      } else {
        if (addToast) {
          addToast(result.error || "Failed to add to cart", { appearance: "error", autoDismiss: true });
        }
      }

      return result;
    } catch (error) {
      if (addToast) {
        addToast("Failed to add to cart", { appearance: "error", autoDismiss: true });
      }
      return { success: false, error: error.message };
    }
  };
};

/**
 * Update cart item quantity via backend API
 * @param {Object} cartItem - Cart item with id
 * @param {number} newQuantity - New quantity
 * @param {Function} addToast - Toast notification function
 */
export const updateCartItemAsync = (cartItem, newQuantity, addToast) => {
  return async dispatch => {
    try {
      const cartItemId = cartItem.cartItemId || cartItem.id;
      const result = await cartService.updateCartItem(cartItemId, newQuantity);

      if (result.success) {
        // Fetch updated cart from backend
        dispatch(fetchCart());

        if (addToast) {
          if (result.removed) {
            addToast("Removed from Cart", { appearance: "warning", autoDismiss: true });
          } else {
            addToast("Cart Updated", { appearance: "success", autoDismiss: true });
          }
        }
      } else {
        if (addToast) {
          addToast(result.error || "Failed to update cart", { appearance: "error", autoDismiss: true });
        }
      }

      return result;
    } catch (error) {
      if (addToast) {
        addToast("Failed to update cart", { appearance: "error", autoDismiss: true });
      }
      return { success: false, error: error.message };
    }
  };
};

/**
 * Increase cart item quantity
 * @param {Object} cartItem - Cart item to increase
 * @param {Function} addToast - Toast notification function
 */
export const increaseQuantityAsync = (cartItem, addToast) => {
  return async dispatch => {
    const newQuantity = (cartItem.quantity || 1) + 1;
    return dispatch(updateCartItemAsync(cartItem, newQuantity, addToast));
  };
};

/**
 * Decrease cart item quantity via backend API
 * @param {Object} cartItem - Cart item to decrease
 * @param {Function} addToast - Toast notification function
 */
export const decreaseQuantityAsync = (cartItem, addToast) => {
  return async dispatch => {
    const newQuantity = (cartItem.quantity || 1) - 1;

    if (newQuantity <= 0) {
      // Remove item if quantity becomes 0
      return dispatch(removeFromCartAsync(cartItem, addToast));
    }

    return dispatch(updateCartItemAsync(cartItem, newQuantity, addToast));
  };
};

/**
 * Remove item from cart via backend API
 * @param {Object} cartItem - Cart item to remove
 * @param {Function} addToast - Toast notification function
 */
export const removeFromCartAsync = (cartItem, addToast) => {
  return async dispatch => {
    try {
      const cartItemId = cartItem.cartItemId || cartItem.id;
      const result = await cartService.removeFromCart(cartItemId);

      if (result.success) {
        // Fetch updated cart from backend
        dispatch(fetchCart());

        if (addToast) {
          addToast("Removed from Cart", { appearance: "warning", autoDismiss: true });
        }
      } else {
        if (addToast) {
          addToast(result.error || "Failed to remove item", { appearance: "error", autoDismiss: true });
        }
      }

      return result;
    } catch (error) {
      if (addToast) {
        addToast("Failed to remove item", { appearance: "error", autoDismiss: true });
      }
      return { success: false, error: error.message };
    }
  };
};

/**
 * Clear entire cart via backend API
 * @param {Function} addToast - Toast notification function
 */
export const clearCartAsync = (addToast) => {
  return async dispatch => {
    try {
      const result = await cartService.clearCart();

      if (result.success) {
        dispatch({ type: DELETE_ALL_FROM_CART });

        if (addToast) {
          addToast("Cart Cleared", { appearance: "warning", autoDismiss: true });
        }
      } else {
        if (addToast) {
          addToast(result.error || "Failed to clear cart", { appearance: "error", autoDismiss: true });
        }
      }

      return result;
    } catch (error) {
      if (addToast) {
        addToast("Failed to clear cart", { appearance: "error", autoDismiss: true });
      }
      return { success: false, error: error.message };
    }
  };
};

// ==========================================
// Legacy actions (kept for backward compatibility)
// These work with local Redux state only
// ==========================================

/**
 * Add to cart (local Redux only)
 * @deprecated Use addToCartAsync for backend integration
 */
export const addToCart = (
  item,
  addToast,
  quantityCount,
  selectedProductColor,
  selectedProductSize
) => {
  return dispatch => {
    if (addToast) {
      addToast("Added To Cart", { appearance: "success", autoDismiss: true });
    }
    dispatch({
      type: ADD_TO_CART,
      payload: {
        ...item,
        quantity: quantityCount,
        selectedProductColor: selectedProductColor
          ? selectedProductColor
          : item.selectedProductColor
          ? item.selectedProductColor
          : null,
        selectedProductSize: selectedProductSize
          ? selectedProductSize
          : item.selectedProductSize
          ? item.selectedProductSize
          : null
      }
    });
  };
};

/**
 * Decrease quantity (local Redux only)
 * @deprecated Use decreaseQuantityAsync for backend integration
 */
export const decreaseQuantity = (item, addToast) => {
  return dispatch => {
    if (addToast) {
      addToast("Item Decremented From Cart", {
        appearance: "warning",
        autoDismiss: true
      });
    }
    dispatch({ type: DECREASE_QUANTITY, payload: item });
  };
};

/**
 * Delete from cart (local Redux only)
 * @deprecated Use removeFromCartAsync for backend integration
 */
export const deleteFromCart = (item, addToast) => {
  return dispatch => {
    if (addToast) {
      addToast("Removed From Cart", { appearance: "error", autoDismiss: true });
    }
    dispatch({ type: DELETE_FROM_CART, payload: item });
  };
};

/**
 * Delete all from cart (local Redux only)
 * @deprecated Use clearCartAsync for backend integration
 */
export const deleteAllFromCart = addToast => {
  return dispatch => {
    if (addToast) {
      addToast("Removed All From Cart", {
        appearance: "error",
        autoDismiss: true
      });
    }
    dispatch({ type: DELETE_ALL_FROM_CART });
  };
};

/**
 * Get stock of cart item
 */
export const cartItemStock = (item, color, size) => {
  if (item.stock) {
    return item.stock;
  } else if (item.variation) {
    const colorMatch = item.variation.filter(single => single.color === color)[0];
    if (colorMatch && colorMatch.size) {
      const sizeMatch = colorMatch.size.filter(single => single.name === size)[0];
      return sizeMatch ? sizeMatch.stock : 0;
    }
  }
  return 999; // Default high stock for products without stock tracking
};
